import React from 'react';
import Icon from '../../../components/AppIcon';
import assignmentService from '../../../services/assignmentService';

const ReviewerPool = ({ reviewers, selectedReviewer, onSelectReviewer, targetWorkload, selectedPaper }) => {
  const getWorkloadColor = (workload) => {
    if (workload > targetWorkload + 2) return 'text-error ';
    if (workload > targetWorkload) return 'text-accent';
    if (workload < targetWorkload - 2) return 'text-primary';
    return 'text-success';
  };

  const getWorkloadPercentage = (workload) => {
    if (targetWorkload === 0) return 0;
    return Math.min((workload / (targetWorkload * 1.5)) * 100, 100);
  };

  const getExpertiseMatch = (reviewer) => {
    if (!selectedPaper) return null;
    return assignmentService?.calculateExpertiseMatch(
      selectedPaper?.keywords,
      reviewer?.subjectAreas
    );
  };

  const hasConflict = (reviewer) => {
    if (!selectedPaper) return false;
    const conflicts = assignmentService?.detectConflicts(selectedPaper, reviewer);
    return conflicts?.length > 0;
  };

  if (!reviewers?.length) {
    return (
      <div className="p-8 text-center">
        <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">No reviewers available</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-border">
      {reviewers?.map((reviewer) => {
        const expertiseMatch = getExpertiseMatch(reviewer);
        const conflict = hasConflict(reviewer);

        return (
          <div
            key={reviewer?.id}
            className={`p-4 cursor-pointer transition-colors hover:bg-muted/50 ${
              selectedReviewer?.id === reviewer?.id ? 'bg-primary-subtle' : ''
            } ${conflict ? 'opacity-50' : ''}`}
            onClick={() => !conflict && onSelectReviewer(reviewer)}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-foreground">
                  {reviewer?.firstName} {reviewer?.lastName}
                </h3>
                <p className="text-xs text-muted-foreground">{reviewer?.institution}</p>
              </div>
              {conflict && (
                <Icon name="AlertTriangle" size={16} className="text-error ml-2" />
              )}
            </div>

            {/* Workload Meter */}
            <div className="mb-3">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground">Workload</span>
                <span className={`font-medium ${getWorkloadColor(reviewer?.currentWorkload)}`}>
                  {reviewer?.currentWorkload} papers
                </span>
              </div>
              <div className="w-full bg-muted h-2">
                <div
                  className={`h-2 transition-all ${
                    reviewer?.currentWorkload > targetWorkload + 2
                      ? 'bg-error'
                      : reviewer?.currentWorkload > targetWorkload
                      ? 'bg-warning' :'bg-success'
                  }`}
                  style={{ width: `${getWorkloadPercentage(reviewer?.currentWorkload)}%` }}
                />
              </div>
            </div>

            {/* Expertise Match (when paper selected) */}
            {expertiseMatch !== null && (
              <div className="mb-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Expertise Match</span>
                  <span className={`font-medium ${
                    expertiseMatch >= 60 ? 'text-success' :
                    expertiseMatch >= 30 ? 'text-warning': 'text-error '
                  }`}>
                    {expertiseMatch}%
                  </span>
                </div>
              </div>
            )}

            {/* Subject Areas */}
            {reviewer?.subjectAreas?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {reviewer?.subjectAreas?.slice(0, 3)?.map((area, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-2 py-0.5 text-xs bg-primary/10 text-primary"
                  >
                    {area}
                  </span>
                ))}
                {reviewer?.subjectAreas?.length > 3 && (
                  <span className="text-xs text-muted-foreground">+{reviewer?.subjectAreas?.length - 3}</span>
                )}
              </div>
            )}

            {/* Experience Level */}
            {reviewer?.experienceLevel && (
              <div className="mt-2 flex items-center">
                <Icon name="Award" size={12} className="text-muted-foreground mr-1" />
                <span className="text-xs text-muted-foreground capitalize">
                  {reviewer?.experienceLevel}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ReviewerPool;
